import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { supabase } from './supabaseClient';
import { formatPrice, getCurrency, initializeCurrency } from './utils/currency';
import { searchAirports } from './services/flightApiService';
import './HomePage.css';
import './AirportSuggestions.css';

/** Partner airline logos (homepage marquee — duplicated in JSX for infinite scroll). */
const PARTNER_LOGO_URLS = [
  'https://wnsyglbyunngvlcpdhju.supabase.co/storage/v1/object/public/Images/Untitled-design-2026-02-03T092709-169-removebg-preview.png',
  'https://wnsyglbyunngvlcpdhju.supabase.co/storage/v1/object/public/Images/Untitled-design-2026-02-03T092741-975-removebg-preview.png',
  'https://wnsyglbyunngvlcpdhju.supabase.co/storage/v1/object/public/Images/Untitled-design-2026-02-03T092814-782-removebg-preview.png',
  'https://wnsyglbyunngvlcpdhju.supabase.co/storage/v1/object/public/Images/Untitled-design-2026-02-03T092833-620-removebg-preview.png',
  'https://wnsyglbyunngvlcpdhju.supabase.co/storage/v1/object/public/Images/Untitled-design-2026-02-03T092900-514-removebg-preview.png',
  'https://wnsyglbyunngvlcpdhju.supabase.co/storage/v1/object/public/Images/Untitled-design-2026-02-03T092926-028-removebg-preview.png',
  'https://wnsyglbyunngvlcpdhju.supabase.co/storage/v1/object/public/Images/Untitled-design-2026-02-03T092948-429-removebg-preview.png',
  'https://wnsyglbyunngvlcpdhju.supabase.co/storage/v1/object/public/Images/Untitled-design-2026-02-03T093009-927-removebg-preview.png',
  'https://wnsyglbyunngvlcpdhju.supabase.co/storage/v1/object/public/Images/11.png',
  'https://wnsyglbyunngvlcpdhju.supabase.co/storage/v1/object/public/Images/12.png',
  'https://wnsyglbyunngvlcpdhju.supabase.co/storage/v1/object/public/Images/13.png',
  'https://wnsyglbyunngvlcpdhju.supabase.co/storage/v1/object/public/Images/14.png',
  'https://wnsyglbyunngvlcpdhju.supabase.co/storage/v1/object/public/Images/15.png',
];

/** Homepage "Exclusive Deals" — static sample prices (NGN base for formatPrice); no live API. */
function buildHardcodedExclusiveDeals() {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const dateStr = tomorrow.toISOString().split('T')[0];
  const validityDate = new Date();
  validityDate.setDate(validityDate.getDate() + 30);
  const validityLabel = validityDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const demoFlight = (from, to, airline, priceNgn) => ({
    id: `demo-${from}-${to}`,
    airline,
    departureAirport: from,
    arrivalAirport: to,
    price: { amount: priceNgn, currency: 'NGN' },
  });

  return [
    {
      from: 'LOS',
      to: 'DXB',
      badge: 'Best Deal',
      fromName: 'Lagos',
      toName: 'Dubai',
      currentPrice: 485000,
      validityDate: validityLabel,
      flight: demoFlight('LOS', 'DXB', 'Emirates', 485000),
      searchData: {
        tripType: 'one-way',
        from: 'LOS',
        to: 'DXB',
        departureDate: dateStr,
        returnDate: '',
        passengers: '1',
        travelClass: 'economy',
      },
    },
    {
      from: 'ABV',
      to: 'LHR',
      badge: 'Popular',
      fromName: 'Abuja',
      toName: 'London',
      currentPrice: 520000,
      validityDate: validityLabel,
      flight: demoFlight('ABV', 'LHR', 'British Airways', 520000),
      searchData: {
        tripType: 'one-way',
        from: 'ABV',
        to: 'LHR',
        departureDate: dateStr,
        returnDate: '',
        passengers: '1',
        travelClass: 'economy',
      },
    },
    {
      from: 'LOS',
      to: 'ACC',
      badge: 'New',
      fromName: 'Lagos',
      toName: 'Accra',
      currentPrice: 310000,
      validityDate: validityLabel,
      flight: demoFlight('LOS', 'ACC', 'Air Peace', 310000),
      searchData: {
        tripType: 'one-way',
        from: 'LOS',
        to: 'ACC',
        departureDate: dateStr,
        returnDate: '',
        passengers: '1',
        travelClass: 'economy',
      },
    },
  ];
}

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tripType, setTripType] = useState('one-way');
  const [searchData, setSearchData] = useState({
    from: '',
    to: '',
    departureDate: '',
    returnDate: '',
    passengers: '1',
    travelClass: 'economy'
  });
  const [multiCityRoutes, setMultiCityRoutes] = useState([
    { from: '', to: '', date: '' }
  ]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [currency, setCurrency] = useState(null);
  const [exclusiveDeals, setExclusiveDeals] = useState([]);
  const [dealsLoading, setDealsLoading] = useState(true);
  const [fromSuggestions, setFromSuggestions] = useState([]);
  const [toSuggestions, setToSuggestions] = useState([]);
  const [showFromSuggestions, setShowFromSuggestions] = useState(false);
  const [showToSuggestions, setShowToSuggestions] = useState(false);
  const [multiCitySuggestions, setMultiCitySuggestions] = useState({});
  const [showMultiCitySuggestions, setShowMultiCitySuggestions] = useState({});
  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const multiCitySearchTimeoutRef = useRef(null);

  // Initialize currency on component mount - ALWAYS DETECT FRESH
  useEffect(() => {
    const initCurrency = async () => {
      try {
        console.log('HomePage: Initializing currency with fresh detection...');
        
        // Force fresh detection (don't use cache on first load)
        const detectedCurrency = await initializeCurrency(true);
        setCurrency(detectedCurrency);
        
        console.log('HomePage: Currency initialized:', {
          code: detectedCurrency.code,
          symbol: detectedCurrency.symbol,
          name: detectedCurrency.name
        });
      } catch (error) {
        console.error('HomePage: Error initializing currency:', error);
        const fallbackCurrency = getCurrency();
        setCurrency(fallbackCurrency);
        console.log('HomePage: Using fallback currency:', {
          code: fallbackCurrency.code,
          symbol: fallbackCurrency.symbol
        });
      }
    };
    
    // Initialize immediately
    initCurrency();

    // Listen for currency detection events
    const handleCurrencyDetected = (event) => {
      console.log('HomePage: Currency detected:', event.detail);
      if (event.detail.currency) {
        setCurrency(event.detail.currency);
      }
    };
    
    // Listen for currency initialization from App
    const handleCurrencyInit = (event) => {
      console.log('HomePage: Received currency from App:', event.detail);
      setCurrency(event.detail);
    };
    
    window.addEventListener('currencyDetected', handleCurrencyDetected);
    window.addEventListener('currencyInitialized', handleCurrencyInit);

    return () => {
      window.removeEventListener('currencyDetected', handleCurrencyDetected);
      window.removeEventListener('currencyInitialized', handleCurrencyInit);
    };
  }, []);

  // Prefill form when navigating from Modify Search
  useEffect(() => {
    const incomingSearchData = location.state?.searchData;
    if (incomingSearchData) {
      console.log('HomePage: Prefilling form with search data:', incomingSearchData);
      
      // Helper function to convert date to YYYY-MM-DD format
      const convertDateToInputFormat = (dateStr) => {
        if (!dateStr) return '';
        // If already in YYYY-MM-DD format, return as is
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return dateStr;
        }
        // Try to parse various date formats
        try {
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          }
        } catch (e) {
          console.error('Error converting date:', dateStr, e);
        }
        return '';
      };
      
      // Set trip type
      if (incomingSearchData.tripType) {
        setTripType(incomingSearchData.tripType);
      }
      
      // Prefill search data
      const newSearchData = {
        from: incomingSearchData.from || '',
        to: incomingSearchData.to || '',
        departureDate: convertDateToInputFormat(incomingSearchData.departureDate),
        returnDate: convertDateToInputFormat(incomingSearchData.returnDate),
        passengers: incomingSearchData.passengers || '1',
        travelClass: incomingSearchData.travelClass || 'economy'
      };
      setSearchData(newSearchData);
      
      // Prefill multi-city routes if applicable
      if (incomingSearchData.tripType === 'multi-city' && incomingSearchData.multiCityRoutes) {
        const formattedRoutes = incomingSearchData.multiCityRoutes.map(route => ({
          from: route.from || '',
          to: route.to || '',
          date: convertDateToInputFormat(route.date)
        }));
        setMultiCityRoutes(formattedRoutes);
      }
      
      // Clear location state to prevent re-prefilling on re-renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchData({
      ...searchData,
      [name]: value
    });

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Search airports when typing in from/to fields (with debounce)
    if (name === 'from') {
      if (value.length >= 2) {
        // Show suggestions dropdown
        setShowFromSuggestions(true);
        // Debounce the search to avoid too many API calls
        searchTimeoutRef.current = setTimeout(() => {
          searchAirportsForField(value, 'from');
        }, 300); // Wait 300ms after user stops typing
      } else {
        setFromSuggestions([]);
        setShowFromSuggestions(false);
      }
    }

    if (name === 'to') {
      if (value.length >= 2) {
        // Show suggestions dropdown
        setShowToSuggestions(true);
        // Debounce the search to avoid too many API calls
        searchTimeoutRef.current = setTimeout(() => {
          searchAirportsForField(value, 'to');
        }, 300); // Wait 300ms after user stops typing
      } else {
        setToSuggestions([]);
        setShowToSuggestions(false);
      }
    }
  };

  const searchAirportsForField = async (query, field) => {
    try {
      console.log(`🔍 Searching airports for ${field}:`, query);
      const result = await searchAirports(query);
      console.log(`✅ Airport search result for ${field}:`, result);
      if (result.success && result.data) {
        if (field === 'from') {
          setFromSuggestions(result.data);
          setShowFromSuggestions(true);
        } else {
          setToSuggestions(result.data);
          setShowToSuggestions(true);
        }
      } else {
        console.error(`❌ Airport search failed for ${field}:`, result.error);
        if (field === 'from') {
          setFromSuggestions([]);
        } else {
          setToSuggestions([]);
        }
      }
    } catch (error) {
      console.error('Error searching airports:', error);
      if (field === 'from') {
        setFromSuggestions([]);
      } else {
        setToSuggestions([]);
      }
    }
  };

  const selectAirport = (airport, field) => {
    // Format: "City Name (IATA)" or just use the name with code
    const displayValue = airport.iataCode 
      ? `${airport.address?.cityName || airport.name} (${airport.iataCode})`
      : airport.name;
    
    setSearchData({
      ...searchData,
      [field]: displayValue
    });

    if (field === 'from') {
      setShowFromSuggestions(false);
      setFromSuggestions([]);
    } else {
      setShowToSuggestions(false);
      setToSuggestions([]);
    }
  };

  const handleAddMultiCityRoute = () => {
    setMultiCityRoutes(prev => [...prev, { from: '', to: '', date: '' }]);
  };

  const handleMultiCityChange = (index, field, value) => {
    const updated = [...multiCityRoutes];
    updated[index][field] = value;
    setMultiCityRoutes(updated);

    if (field !== 'from' && field !== 'to') return;

    const key = `${index}-${field}`;
    if (value.length >= 2) {
      setShowMultiCitySuggestions((prev) => ({ ...prev, [key]: true }));
      if (multiCitySearchTimeoutRef.current) {
        clearTimeout(multiCitySearchTimeoutRef.current);
      }
      multiCitySearchTimeoutRef.current = setTimeout(() => {
        searchAirportsForMultiCity(value, index, field);
      }, 300);
    } else {
      setMultiCitySuggestions((prev) => ({ ...prev, [key]: [] }));
      setShowMultiCitySuggestions((prev) => ({ ...prev, [key]: false }));
    }
  };

  const searchAirportsForMultiCity = async (query, index, field) => {
    const key = `${index}-${field}`;
    try {
      const result = await searchAirports(query);
      if (result.success && Array.isArray(result.data)) {
        setMultiCitySuggestions((prev) => ({
          ...prev,
          [key]: result.data,
        }));
        setShowMultiCitySuggestions((prev) => ({
          ...prev,
          [key]: true,
        }));
      } else {
        setMultiCitySuggestions((prev) => ({ ...prev, [key]: [] }));
      }
    } catch (error) {
      console.error('Error searching airports:', error);
      setMultiCitySuggestions((prev) => ({ ...prev, [key]: [] }));
    }
  };

  const selectAirportMultiCity = (airport, index, field) => {
    const displayValue = airport.iataCode 
      ? `${airport.address?.cityName || airport.name} (${airport.iataCode})`
      : airport.name;
    
    const updated = [...multiCityRoutes];
    updated[index][field] = displayValue;
    setMultiCityRoutes(updated);

    setShowMultiCitySuggestions(prev => ({
      ...prev,
      [`${index}-${field}`]: false
    }));
    setMultiCitySuggestions(prev => ({
      ...prev,
      [`${index}-${field}`]: []
    }));
  };

  const handleRemoveMultiCityRoute = (index) => {
    if (multiCityRoutes.length > 1) {
      setMultiCityRoutes(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Navigate to results page with search data
    const finalSearchData = {
      tripType,
      ...searchData,
      multiCityRoutes: tripType === 'multi-city' ? multiCityRoutes : null
    };
    navigate('/results', { state: { searchData: finalSearchData } });
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    const emailInput = e.target.querySelector('input[type="email"]');
    const email = emailInput?.value?.trim().toLowerCase();
    
    if (!email) {
      setModalMessage('Please enter a valid email address');
      setShowErrorModal(true);
      return;
    }

    try {
      // Check if email already exists
      const { data: existing } = await supabase
        .from('newsletter_subscriptions')
        .select('id, status')
        .eq('email', email)
        .single();

      if (existing) {
        if (existing.status === 'active') {
          setModalMessage('This email is already subscribed to our newsletter!');
          setShowErrorModal(true);
        } else {
          // Reactivate subscription
          const { error: updateError } = await supabase
            .from('newsletter_subscriptions')
            .update({
              status: 'active',
              unsubscribed_at: null,
              unsubscribe_reason: null,
              updated_at: new Date().toISOString()
            })
            .eq('email', email);

          if (updateError) throw updateError;
          setModalMessage('Successfully resubscribed to our newsletter!');
          setShowSuccessModal(true);
          emailInput.value = '';
        }
      } else {
        // Insert new subscription
        const { error: insertError } = await supabase
          .from('newsletter_subscriptions')
          .insert([
            {
              email: email,
              status: 'active',
              subscription_source: 'website',
              is_verified: true
            }
          ]);

        if (insertError) throw insertError;
        setModalMessage('Successfully subscribed to our newsletter!');
        setShowSuccessModal(true);
        emailInput.value = '';
      }
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      setModalMessage('Failed to subscribe. Please try again later.');
      setShowErrorModal(true);
    }
  };

  // Re-render deals when currency changes
  useEffect(() => {
    if (currency) {
      console.log('HomePage: Currency changed, deals will update with:', currency);
      // Force re-render by updating a dummy state or just let React handle it
    }
  }, [currency]);

  // Exclusive deals: static samples (no Supabase / flight-search on homepage)
  useEffect(() => {
    setExclusiveDeals(buildHardcodedExclusiveDeals());
    setDealsLoading(false);
  }, []);

  return (
    <div className="homepage">
      <Header />
      
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <img 
            src="https://wnsyglbyunngvlcpdhju.supabase.co/storage/v1/object/public/Images/1111111.jpeg" 
            alt="Flight booking background"
            className="hero-image"
            onError={(e) => {
              e.target.src = 'https://wnsyglbyunngvlcpdhju.supabase.co/storage/v1/object/public/Images/1111111.jpeg';
            }}
          />
        </div>
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-headline">Your Journey Starts Here</h1>
            <p className="hero-subtext">
              Discover seamless flight booking crafted exclusively for Nigerian travelers. Compare prices, secure your seat, and travel with confidence—all in one place.
            </p>
          </div>
          
          <div className="search-card">
            {/* Trip Type Toggle */}
            <div className="trip-type-toggle" style={{ display: 'flex', gap: '10px', marginBottom: '20px', justifyContent: 'center' }}>
              <button
                type="button"
                className={`trip-type-button ${tripType === 'one-way' ? 'active' : ''}`}
                onClick={() => setTripType('one-way')}
                style={{
                  padding: '8px 16px',
                  border: '2px solid #017734',
                  background: tripType === 'one-way' ? '#017734' : 'transparent',
                  color: tripType === 'one-way' ? 'white' : '#017734',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                One-Way
              </button>
              <button
                type="button"
                className={`trip-type-button ${tripType === 'round-trip' ? 'active' : ''}`}
                onClick={() => setTripType('round-trip')}
                style={{
                  padding: '8px 16px',
                  border: '2px solid #017734',
                  background: tripType === 'round-trip' ? '#017734' : 'transparent',
                  color: tripType === 'round-trip' ? 'white' : '#017734',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Round-Trip
              </button>
              <button
                type="button"
                className={`trip-type-button ${tripType === 'multi-city' ? 'active' : ''}`}
                onClick={() => setTripType('multi-city')}
                style={{
                  padding: '8px 16px',
                  border: '2px solid #017734',
                  background: tripType === 'multi-city' ? '#017734' : 'transparent',
                  color: tripType === 'multi-city' ? 'white' : '#017734',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Multi-City
              </button>
            </div>

            <form onSubmit={handleSearch} className="search-form">
              {/* One-Way / Round-Trip Form */}
              {(tripType === 'one-way' || tripType === 'round-trip') && (
                <>
                  <div className="search-row">
                    <div className="search-field" style={{ position: 'relative' }}>
                      <label htmlFor="from">From</label>
                      <input
                        type="text"
                        id="from" 
                        name="from" 
                        ref={fromInputRef}
                        value={searchData.from}
                        onChange={handleInputChange}
                        onFocus={() => {
                          if (searchData.from.length >= 2 && fromSuggestions.length > 0) {
                            setShowFromSuggestions(true);
                          }
                        }}
                        onBlur={() => {
                          // Delay to allow click on suggestion
                          setTimeout(() => setShowFromSuggestions(false), 250);
                        }}
                        placeholder="Type city or airport name"
                        required
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          fontSize: '14px'
                        }}
                      />
                      {showFromSuggestions && (
                        <div className="airport-suggestions">
                          {fromSuggestions.length > 0 ? (
                            fromSuggestions.map((airport, index) => (
                              <div
                                key={index}
                                className="suggestion-item"
                                onClick={() => selectAirport(airport, 'from')}
                              >
                                <div className="suggestion-main">
                                  <strong>{airport.address?.cityName || airport.name}</strong>
                                  {airport.iataCode && (
                                    <span className="suggestion-code">({airport.iataCode})</span>
                                  )}
                                </div>
                                <div className="suggestion-sub">
                                  {airport.name}
                                  {airport.address?.countryCode && ` • ${airport.address.countryCode}`}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="suggestion-item" style={{ color: '#666', fontStyle: 'italic', cursor: 'default' }}>
                              {searchData.from.length >= 2 ? 'Searching airports...' : 'Type at least 2 characters to search'}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="search-field" style={{ position: 'relative' }}>
                      <label htmlFor="to">To</label>
                      <input
                        type="text"
                        id="to" 
                        name="to" 
                        ref={toInputRef}
                        value={searchData.to}
                        onChange={handleInputChange}
                        onFocus={() => {
                          if (searchData.to.length >= 2 && toSuggestions.length > 0) {
                            setShowToSuggestions(true);
                          }
                        }}
                        onBlur={() => {
                          // Delay to allow click on suggestion
                          setTimeout(() => setShowToSuggestions(false), 250);
                        }}
                        placeholder="Type city or airport name"
                        required
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          fontSize: '14px'
                        }}
                      />
                      {showToSuggestions && (
                        <div className="airport-suggestions">
                          {toSuggestions.length > 0 ? (
                            toSuggestions.map((airport, index) => (
                              <div
                                key={index}
                                className="suggestion-item"
                                onClick={() => selectAirport(airport, 'to')}
                              >
                                <div className="suggestion-main">
                                  <strong>{airport.address?.cityName || airport.name}</strong>
                                  {airport.iataCode && (
                                    <span className="suggestion-code">({airport.iataCode})</span>
                                  )}
                                </div>
                                <div className="suggestion-sub">
                                  {airport.name}
                                  {airport.address?.countryCode && ` • ${airport.address.countryCode}`}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="suggestion-item" style={{ color: '#666', fontStyle: 'italic', cursor: 'default' }}>
                              {searchData.to.length >= 2 ? 'Searching airports...' : 'Type at least 2 characters to search'}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="search-row">
                    <div className="search-field">
                      <label htmlFor="departureDate">Departure</label>
                      <input
                        type="date"
                        id="departureDate"
                        name="departureDate"
                      value={searchData.departureDate}
                      min={new Date().toISOString().split('T')[0]}
                      onChange={(e) => {
                        const newDeparture = e.target.value;
                        // When departure changes, automatically clear or adjust return if it became invalid
                        setSearchData(prev => {
                          let updatedReturn = prev.returnDate;
                          if (tripType === 'round-trip' && updatedReturn && newDeparture && updatedReturn < newDeparture) {
                            updatedReturn = newDeparture;
                          }
                          return {
                            ...prev,
                            departureDate: newDeparture,
                            returnDate: updatedReturn
                          };
                        });
                      }}
                        required
                      />
                    </div>
                    
                    {tripType === 'round-trip' && (
                      <div className="search-field">
                        <label htmlFor="returnDate">Return</label>
                        <input
                          type="date"
                          id="returnDate"
                          name="returnDate"
                          value={searchData.returnDate}
                          min={searchData.departureDate || new Date().toISOString().split('T')[0]}
                          onChange={(e) => {
                            const newReturn = e.target.value;
                            // Prevent selecting a return date before departure
                            if (searchData.departureDate && newReturn && newReturn < searchData.departureDate) {
                              return;
                            }
                            handleInputChange(e);
                          }}
                          required={tripType === 'round-trip'}
                        />
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Multi-City Form */}
              {tripType === 'multi-city' && (
                <div className="multi-city-form" style={{ marginBottom: '15px' }}>
                  {multiCityRoutes.map((route, index) => (
                    <div key={index} className="multi-city-route" style={{ 
                      marginBottom: '15px', 
                      padding: '15px', 
                      border: '1px solid #e0e0e0', 
                      borderRadius: '8px',
                      backgroundColor: '#f9f9f9'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h4 style={{ margin: 0, color: '#017734' }}>Route {index + 1}</h4>
                        {multiCityRoutes.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveMultiCityRoute(index)}
                            style={{
                              background: '#e74c3c',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <div className="search-row">
                        <div className="search-field" style={{ position: 'relative' }}>
                          <label>From</label>
                          <input
                            type="text"
                            value={route.from}
                            onChange={(e) => handleMultiCityChange(index, 'from', e.target.value)}
                            onFocus={() => {
                              if (route.from.length >= 2) {
                                setShowMultiCitySuggestions((prev) => ({
                                  ...prev,
                                  [`${index}-from`]: true,
                                }));
                              }
                            }}
                            onBlur={() => {
                              setTimeout(() => {
                                setShowMultiCitySuggestions((prev) => ({
                                  ...prev,
                                  [`${index}-from`]: false,
                                }));
                              }, 200);
                            }}
                            placeholder="Type city or airport name"
                            required
                            style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' }}
                          />
                          {showMultiCitySuggestions[`${index}-from`] && route.from.length >= 2 && (
                            <div className="airport-suggestions">
                              {(multiCitySuggestions[`${index}-from`]?.length ?? 0) > 0 ? (
                                multiCitySuggestions[`${index}-from`].map((airport, idx) => (
                                  <div
                                    key={`${airport.iataCode}-${idx}`}
                                    className="suggestion-item"
                                    onClick={() => selectAirportMultiCity(airport, index, 'from')}
                                  >
                                    <div className="suggestion-main">
                                      <strong>{airport.address?.cityName || airport.name}</strong>
                                      {airport.iataCode && (
                                        <span className="suggestion-code">({airport.iataCode})</span>
                                      )}
                                    </div>
                                    <div className="suggestion-sub">
                                      {airport.name}
                                      {airport.address?.countryCode && ` • ${airport.address.countryCode}`}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="suggestion-item" style={{ color: '#666', fontStyle: 'italic', cursor: 'default' }}>
                                  Searching airports…
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="search-field" style={{ position: 'relative' }}>
                          <label>To</label>
                          <input
                            type="text"
                            value={route.to}
                            onChange={(e) => handleMultiCityChange(index, 'to', e.target.value)}
                            onFocus={() => {
                              if (route.to.length >= 2) {
                                setShowMultiCitySuggestions((prev) => ({
                                  ...prev,
                                  [`${index}-to`]: true,
                                }));
                              }
                            }}
                            onBlur={() => {
                              setTimeout(() => {
                                setShowMultiCitySuggestions((prev) => ({
                                  ...prev,
                                  [`${index}-to`]: false,
                                }));
                              }, 200);
                            }}
                            placeholder="Type city or airport name"
                            required
                            style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' }}
                          />
                          {showMultiCitySuggestions[`${index}-to`] && route.to.length >= 2 && (
                            <div className="airport-suggestions">
                              {(multiCitySuggestions[`${index}-to`]?.length ?? 0) > 0 ? (
                                multiCitySuggestions[`${index}-to`].map((airport, idx) => (
                                  <div
                                    key={`${airport.iataCode}-${idx}`}
                                    className="suggestion-item"
                                    onClick={() => selectAirportMultiCity(airport, index, 'to')}
                                  >
                                    <div className="suggestion-main">
                                      <strong>{airport.address?.cityName || airport.name}</strong>
                                      {airport.iataCode && (
                                        <span className="suggestion-code">({airport.iataCode})</span>
                                      )}
                                    </div>
                                    <div className="suggestion-sub">
                                      {airport.name}
                                      {airport.address?.countryCode && ` • ${airport.address.countryCode}`}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="suggestion-item" style={{ color: '#666', fontStyle: 'italic', cursor: 'default' }}>
                                  Searching airports…
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="search-field">
                          <label>Date</label>
                          <input
                            type="date"
                            value={route.date}
                            onChange={(e) => handleMultiCityChange(index, 'date', e.target.value)}
                            required
                            style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={handleAddMultiCityRoute}
                    style={{
                      background: '#27ae60',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '10px 20px',
                      cursor: 'pointer',
                      marginTop: '10px',
                      fontWeight: '500'
                    }}
                  >
                    + Add Another City
                  </button>
                </div>
              )}
                
              <div className="search-row">
                <div className="search-field">
                  <label htmlFor="passengers">Passengers</label>
                  <select 
                    id="passengers" 
                    name="passengers"
                    value={searchData.passengers}
                    onChange={handleInputChange}
                  >
                    <option value="1">1 Passenger</option>
                    <option value="2">2 Passengers</option>
                    <option value="3">3 Passengers</option>
                    <option value="4">4 Passengers</option>
                    <option value="5+">5+ Passengers</option>
                  </select>
                </div>
                
                <div className="search-field">
                  <label htmlFor="travelClass">Class</label>
                  <select 
                    id="travelClass" 
                    name="travelClass"
                    value={searchData.travelClass}
                    onChange={handleInputChange}
                  >
                    <option value="economy">Economy</option>
                    <option value="premium_economy">Premium Economy</option>
                    <option value="business">Business</option>
                    <option value="first">First Class</option>
                  </select>
                </div>
              </div>
              
              <button 
                type="submit" 
                className="search-button"
                style={{
                  backgroundColor: '#017734',
                  color: 'white',
                  border: 'none'
                }}
              >
                Search Flights
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Scrolling Logos Section — CSS marquee; two identical strips so the loop has no gap */}
      <section className="logos-section" aria-label="Partner airlines">
        <div className="logos-container">
          <div className="logos-track">
            <div className="logo-marquee-set">
              {PARTNER_LOGO_URLS.map((src, i) => (
                <div className="logo-item" key={`marquee-a-${i}`}>
                  <img src={src} alt="" loading="lazy" decoding="async" />
                </div>
              ))}
            </div>
            <div className="logo-marquee-set" aria-hidden="true">
              {PARTNER_LOGO_URLS.map((src, i) => (
                <div className="logo-item" key={`marquee-b-${i}`}>
                  <img src={src} alt="" loading="lazy" decoding="async" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Popular Destinations */}
      <section className="destinations-section">
        <div className="section-container">
          <h2 className="section-title">Where Will You Go Next?</h2>
          <div className="destinations-scroll-container">
            <div className="destinations-track">
              <div 
                className="destination-card"
                onClick={() => {
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  const dateStr = tomorrow.toISOString().split('T')[0];
                  
                  const searchData = {
                    tripType: 'one-way',
                    from: 'LOS',
                    to: 'ABV',
                    departureDate: dateStr,
                    returnDate: '',
                    passengers: '1',
                    travelClass: 'economy'
                  };
                  
                  navigate('/results', { state: { searchData } });
                }}
                style={{ cursor: 'pointer' }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                  alt="Lagos to Abuja - Airplane cabin"
                  className="destination-image"
                  loading="lazy"
                />
                <div className="destination-overlay">
                  <h3 className="destination-title">Lagos → Abuja</h3>
                  <p className="destination-subtitle">Fast & Frequent Flights</p>
                </div>
              </div>
              
              <div 
                className="destination-card"
                onClick={() => {
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  const dateStr = tomorrow.toISOString().split('T')[0];
                  
                  const searchData = {
                    tripType: 'one-way',
                    from: 'LOS',
                    to: 'LHR',
                    departureDate: dateStr,
                    returnDate: '',
                    passengers: '1',
                    travelClass: 'economy'
                  };
                  
                  navigate('/results', { state: { searchData } });
                }}
                style={{ cursor: 'pointer' }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                  alt="Lagos to London - London cityscape"
                  className="destination-image"
                  loading="lazy"
                />
                <div className="destination-overlay">
                  <h3 className="destination-title">Lagos → London</h3>
                  <p className="destination-subtitle">Explore Europe's Capital</p>
                </div>
              </div>
              
              <div 
                className="destination-card"
                onClick={() => {
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  const dateStr = tomorrow.toISOString().split('T')[0];
                  
                  const searchData = {
                    tripType: 'one-way',
                    from: 'ABV',
                    to: 'DXB',
                    departureDate: dateStr,
                    returnDate: '',
                    passengers: '1',
                    travelClass: 'economy'
                  };
                  
                  navigate('/results', { state: { searchData } });
                }}
                style={{ cursor: 'pointer' }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                  alt="Abuja to Dubai - Dubai skyline"
                  className="destination-image"
                  loading="lazy"
                />
                <div className="destination-overlay">
                  <h3 className="destination-title">Abuja → Dubai</h3>
                  <p className="destination-subtitle">Luxury Awaits</p>
                </div>
              </div>
              
              <div 
                className="destination-card"
                onClick={() => {
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  const dateStr = tomorrow.toISOString().split('T')[0];
                  
                  const searchData = {
                    tripType: 'one-way',
                    from: 'LOS',
                    to: 'ACC',
                    departureDate: dateStr,
                    returnDate: '',
                    passengers: '1',
                    travelClass: 'economy'
                  };
                  
                  navigate('/results', { state: { searchData } });
                }}
                style={{ cursor: 'pointer' }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                  alt="Lagos to Accra - African cityscape"
                  className="destination-image"
                  loading="lazy"
                />
                <div className="destination-overlay">
                  <h3 className="destination-title">Lagos → Accra</h3>
                  <p className="destination-subtitle">Discover West Africa</p>
                </div>
              </div>
              
              <div 
                className="destination-card"
                onClick={() => {
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  const dateStr = tomorrow.toISOString().split('T')[0];
                  
                  const searchData = {
                    tripType: 'one-way',
                    from: 'LOS',
                    to: 'JNB',
                    departureDate: dateStr,
                    returnDate: '',
                    passengers: '1',
                    travelClass: 'economy'
                  };
                  
                  navigate('/results', { state: { searchData } });
                }}
                style={{ cursor: 'pointer' }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                  alt="Lagos to Johannesburg - City skyline"
                  className="destination-image"
                  loading="lazy"
                />
                <div className="destination-overlay">
                  <h3 className="destination-title">Lagos → Johannesburg</h3>
                  <p className="destination-subtitle">Connect to Southern Africa</p>
                </div>
              </div>
              
              <div 
                className="destination-card"
                onClick={() => {
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  const dateStr = tomorrow.toISOString().split('T')[0];
                  
                  const searchData = {
                    tripType: 'one-way',
                    from: 'ABV',
                    to: 'IST',
                    departureDate: dateStr,
                    returnDate: '',
                    passengers: '1',
                    travelClass: 'economy'
                  };
                  
                  navigate('/results', { state: { searchData } });
                }}
                style={{ cursor: 'pointer' }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                  alt="Abuja to Istanbul - City landscape"
                  className="destination-image"
                  loading="lazy"
                />
                <div className="destination-overlay">
                  <h3 className="destination-title">Abuja → Istanbul</h3>
                  <p className="destination-subtitle">Where Continents Meet</p>
                </div>
              </div>
              
              {/* Duplicate cards for seamless loop */}
              <div 
                className="destination-card"
                onClick={() => {
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  const dateStr = tomorrow.toISOString().split('T')[0];
                  
                  const searchData = {
                    tripType: 'one-way',
                    from: 'LOS',
                    to: 'ABV',
                    departureDate: dateStr,
                    returnDate: '',
                    passengers: '1',
                    travelClass: 'economy'
                  };
                  
                  navigate('/results', { state: { searchData } });
                }}
                style={{ cursor: 'pointer' }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                  alt="Lagos to Abuja - Airplane cabin"
                  className="destination-image"
                  loading="lazy"
                />
                <div className="destination-overlay">
                  <h3 className="destination-title">Lagos → Abuja</h3>
                  <p className="destination-subtitle">Fast & Frequent Flights</p>
                </div>
              </div>
              
              <div 
                className="destination-card"
                onClick={() => {
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  const dateStr = tomorrow.toISOString().split('T')[0];
                  
                  const searchData = {
                    tripType: 'one-way',
                    from: 'LOS',
                    to: 'LHR',
                    departureDate: dateStr,
                    returnDate: '',
                    passengers: '1',
                    travelClass: 'economy'
                  };
                  
                  navigate('/results', { state: { searchData } });
                }}
                style={{ cursor: 'pointer' }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                  alt="Lagos to London - London cityscape"
                  className="destination-image"
                  loading="lazy"
                />
                <div className="destination-overlay">
                  <h3 className="destination-title">Lagos → London</h3>
                  <p className="destination-subtitle">Explore Europe's Capital</p>
                </div>
              </div>
              
              <div 
                className="destination-card"
                onClick={() => {
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  const dateStr = tomorrow.toISOString().split('T')[0];
                  
                  const searchData = {
                    tripType: 'one-way',
                    from: 'ABV',
                    to: 'DXB',
                    departureDate: dateStr,
                    returnDate: '',
                    passengers: '1',
                    travelClass: 'economy'
                  };
                  
                  navigate('/results', { state: { searchData } });
                }}
                style={{ cursor: 'pointer' }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                  alt="Abuja to Dubai - Dubai skyline"
                  className="destination-image"
                  loading="lazy"
                />
                <div className="destination-overlay">
                  <h3 className="destination-title">Abuja → Dubai</h3>
                  <p className="destination-subtitle">Luxury Awaits</p>
                </div>
              </div>
              
              <div 
                className="destination-card"
                onClick={() => {
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  const dateStr = tomorrow.toISOString().split('T')[0];
                  
                  const searchData = {
                    tripType: 'one-way',
                    from: 'LOS',
                    to: 'ACC',
                    departureDate: dateStr,
                    returnDate: '',
                    passengers: '1',
                    travelClass: 'economy'
                  };
                  
                  navigate('/results', { state: { searchData } });
                }}
                style={{ cursor: 'pointer' }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                  alt="Lagos to Accra - African cityscape"
                  className="destination-image"
                  loading="lazy"
                />
                <div className="destination-overlay">
                  <h3 className="destination-title">Lagos → Accra</h3>
                  <p className="destination-subtitle">Discover West Africa</p>
                </div>
              </div>
              
              <div 
                className="destination-card"
                onClick={() => {
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  const dateStr = tomorrow.toISOString().split('T')[0];
                  
                  const searchData = {
                    tripType: 'one-way',
                    from: 'LOS',
                    to: 'JNB',
                    departureDate: dateStr,
                    returnDate: '',
                    passengers: '1',
                    travelClass: 'economy'
                  };
                  
                  navigate('/results', { state: { searchData } });
                }}
                style={{ cursor: 'pointer' }}
              >
                <img 
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                  alt="Lagos to Johannesburg - City skyline"
                  className="destination-image"
                  loading="lazy"
                />
                <div className="destination-overlay">
                  <h3 className="destination-title">Lagos → Johannesburg</h3>
                  <p className="destination-subtitle">Connect to Southern Africa</p>
                </div>
              </div>
              
              <div className="destination-card">
                <img 
                  src="https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                  alt="Abuja to Istanbul - City landscape"
                  className="destination-image"
                  loading="lazy"
                />
                <div className="destination-overlay">
                  <h3 className="destination-title">Abuja → Istanbul</h3>
                  <p className="destination-subtitle">Where Continents Meet</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Strip */}
      <section className="feature-strip">
        <div className="feature-container">
          <div className="feature-card">
            <div className="feature-icon">✈️</div>
            <h3 className="feature-title">Book in Minutes, Not Hours</h3>
            <p className="feature-description">
              Our intuitive platform makes finding and booking flights effortless. No complicated forms, no hidden fees—just straightforward booking that saves you time.
            </p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">🇳🇬</div>
            <h3 className="feature-title">Routes That Matter to You</h3>
            <p className="feature-description">
              From Lagos to London, Abuja to Dubai, and everywhere in between. We connect you to over 200 destinations worldwide with the routes Nigerians travel most.
            </p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">💬</div>
            <h3 className="feature-title">We're Here When You Need Us</h3>
            <p className="feature-description">
              Got a question? Need to change your booking? Our dedicated support team is available 24/7 to help you every step of the way.
            </p>
          </div>
        </div>
      </section>

      {/* Why SymbolFly */}
      <section className="why-section">
        <div className="section-container">
          <h2 className="why-headline">Why Thousands of Travelers Choose SymbolFly</h2>
          <p className="why-subtext">
            We've reimagined flight booking to be faster, safer, and more reliable—because your journey deserves nothing less.
          </p>
          
          <div className="advantages-grid">
            <div className="advantage-item">
              <div className="advantage-icon">🔒</div>
              <h3 className="advantage-title">Your Security is Our Priority</h3>
              <p className="advantage-text">
                Every transaction is encrypted and protected. Your personal information and payment details are safe with industry-leading security measures you can trust.
              </p>
            </div>
            
            <div className="advantage-item">
              <div className="advantage-icon">⚡</div>
              <h3 className="advantage-title">Instant Booking Confirmation</h3>
              <p className="advantage-text">
                No waiting, no uncertainty. Get your flight confirmed instantly and receive your e-ticket immediately via email and SMS.
              </p>
            </div>
            
            <div className="advantage-item">
              <div className="advantage-icon">🌟</div>
              <h3 className="advantage-title">Best Prices, Guaranteed</h3>
              <p className="advantage-text">
                We compare prices across multiple airlines to ensure you always get the best deal. Plus, exclusive discounts you won't find anywhere else.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Special Offers Section */}
      <section className="offers-section">
        <div className="section-container">
          <h2 className="section-title">Don't Miss These Exclusive Deals</h2>
          <p className="offers-subtitle">Save up to 15% on your next trip—book now before these prices disappear</p>
          {(dealsLoading || !currency) ? (
            <div className="offers-grid" style={{ minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <p style={{ color: '#666', fontSize: '1.1rem' }}>
                {!currency ? 'Detecting your location for currency...' : 'Loading exclusive deals...'}
              </p>
            </div>
          ) : exclusiveDeals.length > 0 ? (
            <div className="offers-grid">
              {exclusiveDeals
                .filter(deal => deal.currentPrice && deal.currentPrice > 0)
                .map((deal, index) => {
                  // Get current currency - ALWAYS use detected currency, not hardcoded
                  const userCurrency = currency || getCurrency() || { code: 'NGN', symbol: '₦', name: 'Nigerian Naira' };

                  const currentPriceFormatted = formatPrice(deal.currentPrice, {
                    decimals: 0,
                    currency: userCurrency,
                  });

                  // Extract just the amount (remove symbol and commas for parsing)
                  const priceAmount = currentPriceFormatted.replace(userCurrency.symbol, '').replace(/,/g, '').trim();
                  const numericAmount = parseInt(priceAmount) || 0;

                  return (
                    <div key={index} className="offer-card">
                      {deal.badge && <div className="offer-badge">{deal.badge}</div>}
                      <div className="offer-route">
                        <span className="offer-from">{deal.fromName}</span>
                        <span className="offer-arrow">→</span>
                        <span className="offer-to">{deal.toName}</span>
                      </div>
                      <div className="offer-price">
                        <span className="offer-currency">{userCurrency.symbol}</span>
                        <span className="offer-amount">{numericAmount.toLocaleString()}</span>
                      </div>
                      <p className="offer-dates">Valid until {deal.validityDate}</p>
                      <button 
                        className="offer-button"
                        onClick={() => {
                          // Navigate to results page with search data and selected flight
                          navigate('/results', { 
                            state: { 
                              searchData: deal.searchData,
                              selectedFlight: deal.flight
                            } 
                          });
                        }}
                      >
                        View Details
                      </button>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="offers-grid" style={{ minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <p style={{ color: '#666', fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                  🎯 Great deals are being prepared for you!
                </p>
                <p style={{ color: '#999', fontSize: '0.95rem' }}>
                  Check back soon for exclusive flight offers and special discounts.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="newsletter-modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="newsletter-modal" onClick={(e) => e.stopPropagation()}>
            <div className="newsletter-modal-content">
              <div className="newsletter-modal-icon success-icon">✓</div>
              <h3 className="newsletter-modal-title">Success!</h3>
              <p className="newsletter-modal-message">{modalMessage}</p>
              <button 
                className="newsletter-modal-button"
                onClick={() => setShowSuccessModal(false)}
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="newsletter-modal-overlay" onClick={() => setShowErrorModal(false)}>
          <div className="newsletter-modal" onClick={(e) => e.stopPropagation()}>
            <div className="newsletter-modal-content">
              <div className="newsletter-modal-icon error-icon">✕</div>
              <h3 className="newsletter-modal-title">Oops!</h3>
              <p className="newsletter-modal-message">{modalMessage}</p>
              <button 
                className="newsletter-modal-button"
                onClick={() => setShowErrorModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Newsletter CTA */}
      <section className="newsletter-section">
        <div className="section-container">
          <div className="newsletter-card">
            <div className="newsletter-accent"></div>
            <h2 className="newsletter-headline">Get Exclusive Deals Before Everyone Else</h2>
            <p className="newsletter-subtext">Join thousands of smart travelers and receive special offers, flash sales, and travel tips delivered directly to your inbox.</p>
            <form onSubmit={handleSubscribe} className="newsletter-form">
              <input
                type="email"
                placeholder="Enter your email address"
                className="newsletter-input"
                required
              />
              <button type="submit" className="newsletter-button">
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;

